import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { ErrorNotice, Notice, SelectField, Spinner, formatDate } from '../components/ui';
import { ALL_STATUSES } from '../types';
import type {
  AudienceFilter,
  Newsletter,
  NewsletterAudience,
  NewsletterPreview,
  NewsletterSendResult,
} from '../types';

/**
 * Sending a newsletter to the membership.
 *
 * Its own screen rather than a panel on the composer, because writing and
 * sending are different acts and only one of them cannot be undone. The
 * composer is somewhere to fiddle; this is somewhere to be careful.
 *
 * The order on the page is the order somebody should do it in: see it, send
 * one to yourself, choose who gets it, then send. The test send is above the
 * audience for that reason — reading it in your own mail client is the step
 * people skip, and it is the one that catches a wrong link or a sentence that
 * reads badly on a phone.
 */
export function NewsletterSendPage() {
  const { id } = useParams();
  const newsletterId = Number(id);

  const [newsletter, setNewsletter] = useState<Newsletter | null>(null);
  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [audience, setAudience] = useState<AudienceFilter>({});
  const [reach, setReach] = useState<NewsletterAudience | null>(null);
  const [sent, setSent] = useState<NewsletterSendResult | null>(null);
  const [tested, setTested] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<{ newsletter: Newsletter }>(`/newsletters/${newsletterId}`)
      .then((r) => setNewsletter(r.newsletter))
      .catch(setError);
    api
      .post<NewsletterPreview>(`/newsletters/${newsletterId}/preview`, {})
      .then(setPreview)
      .catch(() => undefined);
  }, [newsletterId]);

  // Recounted whenever the filters change, so the number beside the button is
  // never one somebody read a minute ago.
  useEffect(() => {
    if (newsletter === null || sent !== null) return;

    let cancelled = false;
    setConfirming(false);
    api
      .post<NewsletterAudience>(`/newsletters/${newsletterId}/audience`, { audience })
      .then((result) => {
        if (!cancelled) setReach(result);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [audience, newsletterId, newsletter, sent]);

  async function testSend() {
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ sent_to: string }>(
        `/newsletters/${newsletterId}/test-send`,
        {},
      );
      setTested(result.sent_to);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setError(null);
    setBusy(true);
    try {
      setSent(
        await api.post<NewsletterSendResult>(`/newsletters/${newsletterId}/send`, { audience }),
      );
    } catch (failure) {
      setError(failure);
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  const frame = useMemo(
    () => (
      <iframe
        className="newsletter-frame short"
        title="The newsletter as it will be sent"
        sandbox=""
        srcDoc={`<!doctype html><meta charset="utf-8"><style>html,body{margin:0;background:#f6f7f9}</style>${preview?.html ?? ''}`}
      />
    ),
    [preview?.html],
  );

  if (newsletter === null) {
    return (
      <>
        <ErrorNotice error={error} />
        {error === null ? <Spinner label="Opening the newsletter" /> : null}
      </>
    );
  }

  if (sent !== null) {
    return (
      <>
        <div className="page-head">
          <h1>On its way</h1>
        </div>
        <Notice kind="success">
          Queued for {sent.queued} member{sent.queued === 1 ? '' : 's'}.
        </Notice>
        <div className="card">
          <p>
            They go out on the next send run.{' '}
            <Link to="/email/log">Watch the delivery log</Link> to see them leave, and to see
            anything that failed.
          </p>
          {sent.unreachable > 0 ? (
            <p className="muted">
              {sent.unreachable} member{sent.unreachable === 1 ? ' has' : 's have'} no email address
              and will need a paper copy.
            </p>
          ) : null}
          <p className="muted">
            This newsletter is now closed to editing. The link inside it still opens this issue for
            everybody who received it, so changing the text afterwards would make that page
            something other than what they were sent.
          </p>
          <div className="actions">
            <Link to="/email/newsletters" className="button-link primary">
              Back to newsletters
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (newsletter.status === 'sent') {
    return (
      <>
        <p className="muted small">
          <Link to="/email/newsletters">← Back to newsletters</Link>
        </p>
        <div className="page-head">
          <h1>{newsletter.title}</h1>
        </div>
        <Notice kind="warning">
          This went out on {formatDate(newsletter.sent_at)} and cannot be sent again. Starting a new
          newsletter is the way to say something else.
        </Notice>
      </>
    );
  }

  const count = reach?.recipient_count ?? 0;

  return (
    <>
      <p className="muted small">
        <Link to={`/email/newsletters/${newsletterId}`}>← Back to writing it</Link>
      </p>

      <div className="page-head">
        <h1>Send “{newsletter.title}”</h1>
      </div>

      <ErrorNotice error={error} />

      {(reach?.warnings ?? []).map((warning) => (
        <Notice kind="warning" key={warning}>
          {warning}
        </Notice>
      ))}

      <div className="card">
        <h2>What they will get</h2>
        <p className="hint">
          Subject: <strong>{newsletter.subject}</strong>
        </p>
        {frame}
      </div>

      <div className="card">
        <h2>Send one to yourself first</h2>
        <p className="hint">
          It goes to your own address and nobody else’s, marked as a test, and it can be done as
          many times as you like. Reading it in your own email is what catches a link that does not
          work or a sentence that reads badly on a phone.
        </p>
        {tested !== null ? (
          <Notice kind="success">
            A test copy is queued for {tested}. It goes out on the next send run.
          </Notice>
        ) : null}
        <div className="actions">
          <button type="button" disabled={busy} onClick={() => void testSend()}>
            {busy ? 'Sending…' : tested === null ? 'Send a test to me' : 'Send another test'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Who gets it</h2>
        <div className="filters">
          <SelectField
            label="Membership status"
            value={audience.status ?? ''}
            onChange={(e) => setAudience({ ...audience, status: e.target.value })}
          >
            <option value="">Anyone</option>
            {ALL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="This year's subscription"
            value={audience.payment ?? ''}
            onChange={(e) =>
              setAudience({ ...audience, payment: e.target.value as AudienceFilter['payment'] })
            }
          >
            <option value="">Doesn't matter</option>
            <option value="owing">Renewed but still owing</option>
            <option value="settled">Paid up</option>
            <option value="not_renewed">Not renewed at all this year</option>
          </SelectField>
          <SelectField
            label="Mentors"
            value={audience.is_mentor ?? ''}
            onChange={(e) => setAudience({ ...audience, is_mentor: e.target.value })}
          >
            <option value="">Doesn't matter</option>
            <option value="1">Only mentors</option>
            <option value="0">Only non-mentors</option>
          </SelectField>
        </div>
        <p className="muted small">
          Most newsletters go to everybody, which is what these say as they stand. Members who have
          resigned or died are never included, whatever is chosen here.
        </p>
      </div>

      <div className="card">
        <h2>Send it</h2>

        {reach === null ? (
          <Spinner label="Counting" />
        ) : (
          <>
            <p className="send-count">
              <strong>
                {count} member{count === 1 ? '' : 's'}
              </strong>{' '}
              will receive this.
            </p>

            {reach.via_guardian_count > 0 ? (
              <p className="muted small">
                {reach.via_guardian_count} of them will be reached through a parent or guardian.
              </p>
            ) : null}

            {reach.unreachable_count > 0 ? (
              <details className="hint">
                <summary>
                  {reach.unreachable_count} member{reach.unreachable_count === 1 ? '' : 's'} with no
                  email address will not get it
                </summary>
                <ul>
                  {reach.unreachable.map((person) => (
                    <li key={person.member_id}>
                      <Link to={`/members/${person.member_id}`}>{person.name}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {Object.keys(reach.empty_fields).length > 0 ? (
              <Notice kind="warning">
                Some merge fields would come out blank:{' '}
                {Object.entries(reach.empty_fields)
                  .map(([field, n]) => `${field} for ${n}`)
                  .join(', ')}
                .
              </Notice>
            ) : null}

            {count === 0 ? (
              <Notice kind="warning">
                Nobody matches those filters, so there is nothing to send.
              </Notice>
            ) : confirming ? (
              <>
                <Notice kind="warning">
                  This cannot be undone. Once it is queued the newsletter is closed to editing, and
                  the emails will go out on the next send run.
                </Notice>
                <div className="actions">
                  <button type="button" className="primary" disabled={busy} onClick={() => void send()}>
                    {busy ? 'Sending…' : `Yes — send to ${count} member${count === 1 ? '' : 's'}`}
                  </button>
                  <button type="button" disabled={busy} onClick={() => setConfirming(false)}>
                    No, not yet
                  </button>
                </div>
              </>
            ) : (
              <div className="actions">
                <button type="button" className="primary" onClick={() => setConfirming(true)}>
                  Send to {count} member{count === 1 ? '' : 's'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
